import { Box, Text, Skeleton } from '@chakra-ui/react';
import React, { useCallback, useEffect, useState } from 'react';

import { DbTable, Util } from '@finley-technologies/finley-types';

import {
  AssignmentPreviewJobFragmentGQLType,
  useGetAssignmentPreviewJobsQuery,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { Alert } from 'packages/common-frontend/components/Alert';
import { AssignmentTemplate } from 'packages/loanProduct/ui/AssignmentTemplate';

export type StatusAlertProps = {
  status?: DbTable.ReceivableResponseTapePreviewJobs.Status | null;
  text?: string;
};

export const StatusAlert: React.FC<StatusAlertProps> = ({ status, text }) => {
  switch (status) {
    case 'completed':
      return (
        <Alert status="success" isClosable={false}>
          <Text>{text || `Your tapes are ready to assign`}</Text>
        </Alert>
      );
    case 'error':
      return (
        <Alert status="error" isClosable={false}>
          <Text>
            {text ||
              `An error occurred during the latest assignment process. Review your
            assignment strategy and try again.`}
          </Text>
        </Alert>
      );
    case 'inProgress':
      return (
        <Alert status="warning" isClosable={false}>
          <Text>
            {text ||
              `Your tapes are processing. You will be notified by email when tapes
            are ready to assign.`}
          </Text>
        </Alert>
      );
    case null:
    default:
      return <Skeleton />;
  }
};

export const FallbackAssignmentTemplate: React.FC<{
  status?: DbTable.ReceivableResponseTapePreviewJobs.Status | null;
  children?: React.ReactNode;
}> = ({ children, status }) => {
  return (
    <Box height="100%">
      <AssignmentTemplate>
        <StatusAlert status={status} />
        {children}
      </AssignmentTemplate>
    </Box>
  );
};

export const usePreviewGenerationStatus = ({
  loanProductIds,
  organizationId,
  reportingDate,
  fileVersion,
}: {
  loanProductIds: string[];
  organizationId: string;
  reportingDate?: Util.FullDateStr;
  fileVersion?: number;
}) => {
  const [isCompleted, setIsCompleted] = useState<boolean | null>(null);
  const [latestPreviewIdentifier, setLatestPreviewIdentifier] = useState<
    string | null
  >(null);

  const [
    latestJob,
    setLatestJob,
  ] = useState<AssignmentPreviewJobFragmentGQLType | null>(null);

  const { data, startPolling, stopPolling } = useGetAssignmentPreviewJobsQuery({
    fetchPolicy: 'no-cache',
    variables: {
      loanProductIds,
      organizationId,
      reportingDate,
      fileVersion,
    },
  });

  const FallbackAssignmentTemplateComponent: React.FC<{
    status?: DbTable.ReceivableResponseTapePreviewJobs.Status | null;
    children?: React.ReactNode;
  }> = useCallback(
    ({ status, children: fallbackTemplateChildren }) => {
      return (
        <FallbackAssignmentTemplate
          status={status || status === null ? status : latestJob?.status}
        >
          {fallbackTemplateChildren}
        </FallbackAssignmentTemplate>
      );
    },
    [latestJob?.status],
  );

  const StatusAlertComponent: React.FC<{
    status?: DbTable.ReceivableResponseTapePreviewJobs.Status | null;
    text?: string;
  }> = useCallback(
    ({ status, text }) => {
      return (
        <StatusAlert
          status={status || status === null ? status : latestJob?.status}
          text={text}
        />
      );
    },
    [latestJob?.status],
  );

  useEffect(() => {
    startPolling(5000);
    return () => {
      stopPolling();
    };
  }, [startPolling, stopPolling]);

  useEffect(() => {
    if (data?.assignmentPreviewJobs) {
      const latestPreviewJob =
        data.assignmentPreviewJobs.length > 0
          ? data.assignmentPreviewJobs[0]
          : null;

      // @jendevelops [TEMP] assignment block on in progress will be removed once moving to preview select table to show all jobs
      const currIsCompleted = data.assignmentPreviewJobs.every(
        // every preview job needs to be in terminal status
        ({ status }) =>
          DbTable.ReceivableResponseTapePreviewJobs.terminalStatuses.includes(
            status,
          ),
      );
      setLatestJob(latestPreviewJob);
      setLatestPreviewIdentifier(latestPreviewJob?.previewIdentifier ?? null);
      setIsCompleted(currIsCompleted);
    }
  }, [data]);

  // @jendevelops TODO surface if latest preview resulted in error

  return {
    isCompleted,
    latestPreviewIdentifier,
    latestJob,
    StatusAlert: StatusAlertComponent,
    FallbackComponent: function FallbackComponent() {
      return (
        <FallbackAssignmentTemplateComponent
          status={latestJob?.status ?? null}
        />
      );
    },
  };
};
